import React from 'react';
import Modal from "../../components/modal/modal";
import axiosInstance from "../../AxiosInstance";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";
import {DangerButton} from "../../components/input/inputs";
import {closeOcDetail, openNecDetail} from '../bus/modalApi';
import premium from "../invoice_detail/invoiceDetailModal.module.css";
import { PremiumCardRow } from '../shared/PremiumCardRow.jsx';
import { formatCurrencyAmount, purchaseOrderInvoiceAmountLeft } from "../../utils/currency";

function getNecPendingAmountLabel(detail, bills) {
    const invoicesTotal = (bills ?? []).reduce((acc, bill) => acc + Number(bill?.total || 0), 0);
    const necTotal = Number(detail?.total || 0);
    return formatCurrencyAmount(detail?.currency, Math.abs(invoicesTotal - necTotal));
}

function getAssociatedCpeRows(bills) {
    if (!bills?.length) return '-';
    return bills.map((bill) => {
        const sn = bill?.sn || 'Comprobante';
        return `${sn}: ${formatCurrencyAmount(bill?.currency, bill?.total)}`.trim();
    });
}

export default function OCDetailModal({show, setter, oc, userType}) {
    const systemSettings = JSON.parse(localStorage.getItem('system_settings'));
    const amountLeft = oc?.amount_left ?? purchaseOrderInvoiceAmountLeft(oc);

    const downloadOC = () => {
        axiosInstance.get(`purchase_order/${oc.id}/download-pdf/`, {
            responseType: 'blob'
        }).then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', `${oc.number}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        });
    }

    const showOCDetails = (detail) => {
        const bills = oc.bills.filter((x) => x.order_detail?.id === detail?.id);
        closeOcDetail();
        if (!bills.length) {
            openNecDetail({ oc, nec: detail, bill: null, userType });
            return;
        }
        if (bills.length > 1) {
            openNecDetail({ oc, nec: detail, bill: null, userType });
            return;
        }
        let found_bill = { ...bills[0] };
        found_bill = { ...found_bill, sunat_status: -1, conformity_status: -1 };
        axiosInstance.get(`sunat/${found_bill.id}/`).then((response) => {
            let bill = found_bill;
            if (response?.status === 200 && response?.data) {
                const response_data = response.data;
                bill = {
                    ...found_bill,
                    sunat_status: response_data.ose_sunat_status,
                    conformity_status: response_data.conformity_status,
                };
            }
            openNecDetail({ oc, nec: detail, bill, userType });
        }).catch(() => {
            openNecDetail({
                oc,
                nec: detail,
                bill: { ...found_bill, sunat_status: -2, conformity_status: -2 },
                userType,
            });
        });
    }

    const title = oc?.number ? `Detalles de la Orden de Compra [${oc.number}]` : 'Detalles de la Orden de Compra';

    return (
        <Modal
            size={'xl'}
            show={show}
            setter={setter}
            hideClose={true}
            name={title}
            headerClassName={premium.headerPremium}
            titleClassName={premium.titlePremium}
            dialogClassName={premium.dialogWide}
        >
            <div className={premium.bodyWrap}>
                <div className={premium.modalSubtitle}>Datos de la orden</div>
                <div className={premium.compactGrid}>
                    <PremiumCardRow label="Orden de Compra:">{oc.number}</PremiumCardRow>
                    <PremiumCardRow label="Monto:">{formatCurrencyAmount(oc.currency, oc.total)}</PremiumCardRow>
                    <PremiumCardRow label={`RUC del ${userType}:`}>
                        {userType === "cliente" ? oc.client.ruc : oc?.provider?.ruc}
                    </PremiumCardRow>
                    <PremiumCardRow label="Fecha de Emisión:">{oc.date}</PremiumCardRow>
                    <PremiumCardRow label="Moneda:">{oc.currency.name}</PremiumCardRow>
                    <PremiumCardRow label={`Razón Social del ${userType}:`} span2>
                        {userType === "cliente" ? oc.client.name : oc?.provider?.name}
                    </PremiumCardRow>
                </div>

                <div className={premium.modalSubtitle}>Observaciones</div>
                <div className={premium.paymentObservationsBox}>{oc.observations || '-'}</div>

                <div className="mb-3">
                    <button type="button" className={premium.linkLike} onClick={downloadOC}>
                        <FontAwesomeIcon icon={faDownload} className="pe-2" />
                        Descargar OC (PDF)
                    </button>
                </div>

                <div className={premium.modalSubtitle}>Detalle NEC</div>
                <table className={premium.modernTable}>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Identificador</th>
                            <th>Tipo</th>
                            <th>CPE Asociado</th>
                            <th>Estado</th>
                            <th>Monto Total NEC</th>
                            <th>Monto Pendiente NEC</th>
                            <th>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {oc.order_details?.map((detail, index) => {
                            const bills = oc.bills.filter((x) => x.order_detail?.id === detail?.id);
                            const cpeRows = getAssociatedCpeRows(bills);
                            const estadoLabel =
                                bills.length === 0
                                    ? 'Sin asociar'
                                    : bills.length === 1
                                      ? 'Asociado'
                                      : `Asociado ${bills.length} comprobantes`;

                            return (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{detail.nec_number}</td>
                                    <td>NEC</td>
                                    <td>
                                        {Array.isArray(cpeRows)
                                            ? cpeRows.map((row) => <div key={row}>{row}</div>)
                                            : cpeRows}
                                    </td>
                                    <td>{estadoLabel}</td>
                                    <td>{formatCurrencyAmount(detail.currency, detail.total)}</td>
                                    <td>{getNecPendingAmountLabel(detail, bills)}</td>
                                    <td>
                                        <div
                                            role="button"
                                            className="d-inline-flex justify-content-center"
                                            tabIndex={0}
                                            onClick={() => showOCDetails(detail)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') showOCDetails(detail);
                                            }}
                                        >
                                            <div style={{
                                                width: "28px",
                                                height: "28px",
                                                borderRadius: '6px',
                                                backgroundColor: systemSettings.main_color ? systemSettings.main_color : "#FF1235",
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={faMagnifyingGlass} color={'#FFF'}/>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className={premium.tableSummaryRow}>
                    <span>Monto por facturar</span>
                    <strong>{formatCurrencyAmount(oc.currency, amountLeft)}</strong>
                </div>
            </div>

            <div className={premium.footerBar}>
                <div className={premium.footerRight}>
                    <DangerButton onClick={() => {
                        setter(false);
                    }} name={'Cerrar'}/>
                </div>
            </div>
        </Modal>
    )
}
